import {
  doc,
  getDoc,
  runTransaction,
  serverTimestamp,
  type Firestore,
  type Transaction,
  type DocumentReference,
} from 'firebase/firestore'
import { isCountedChecklist, CAP_KEY_TO_FEATURE_KEY } from '../config/elementCounting'
import { WORKSPACE_COUNT_HARD_CAPS } from '../config/workspaceLimitCaps'
import { getWorkspaceLimitValue } from '../utils/workspaceLimits'
import type { WorkspaceLimits, TimelineBoardKey } from '../types/schema'
import { getNlsString } from '../nls/nlsRuntime'

import { isSyntheticParentId } from '../config/elementCounting'

export { isSyntheticParentId }

export interface ChecklistCounterStepParams {
  workspaceId: string
  board: string
  parentContainerId: string
  checklistId: string
  nodeKind?: string
  isArchived?: boolean
  fallbackLimits?: WorkspaceLimits | null
}

export interface ChecklistCounterStepResult {
  isCounted: boolean
  applyWrites: () => void
}

export async function readChecklistCounterSteps(
  transaction: Transaction,
  db: Firestore,
  params: ChecklistCounterStepParams,
): Promise<ChecklistCounterStepResult> {
  const isCounted = isCountedChecklist({
    nodeKind: params.nodeKind,
    isArchived: params.isArchived,
    board: params.board,
    parentContainerId: params.parentContainerId,
  })

  if (!isCounted) {
    return {
      isCounted: false,
      applyWrites: () => {},
    }
  }

  const effectiveLimitsRef = doc(db, 'workspaceEffectiveLimits', params.workspaceId)
  const boardCounterRef = doc(db, 'workspaces', params.workspaceId, 'elementCounters', params.board)
  const hasRealParent = params.parentContainerId && !isSyntheticParentId(params.parentContainerId)
  const parentCounterRef = hasRealParent
    ? doc(db, 'workspaces', params.workspaceId, 'elementParentCounters', params.parentContainerId)
    : null

  // All reads FIRST
  const effectiveLimitsSnap = await transaction.get(effectiveLimitsRef)
  const boardCounterSnap = await transaction.get(boardCounterRef)
  const parentCounterSnap = parentCounterRef ? await transaction.get(parentCounterRef) : null

  // Resolve limits
  const limitsData = effectiveLimitsSnap.exists() ? (effectiveLimitsSnap.data() as Record<string, unknown>) : null
  const boardSoftCaps = (limitsData?.boardSoftCaps ?? {}) as Record<string, Record<string, number>>
  const softCaps = (limitsData?.softCaps ?? {}) as Record<string, number>
  const fallbackLimits = params.fallbackLimits

  // Check board limit
  const boardLimit = boardSoftCaps[params.board]?.maxChecklistsPerBoard
    ?? softCaps.maxChecklistsPerBoard
    ?? (fallbackLimits ? getWorkspaceLimitValue(fallbackLimits, 'maxChecklistsPerBoard', params.board as TimelineBoardKey) : WORKSPACE_COUNT_HARD_CAPS.maxChecklistsPerBoard)

  const bData = boardCounterSnap.exists() ? (boardCounterSnap.data() as Record<string, unknown>) : {}
  const currentBoardChecklists = typeof bData.checklists === 'number' ? bData.checklists : 0

  if (boardLimit !== -1 && currentBoardChecklists + 1 > boardLimit) {
    if (typeof window !== 'undefined') {
      window.dispatchEvent(new CustomEvent('linkhub:paywall-required', {
        detail: {
          featureKey: CAP_KEY_TO_FEATURE_KEY.maxChecklistsPerBoard,
          limit: boardLimit,
          currentUsage: currentBoardChecklists,
        },
      }))
    }
    const message = getNlsString('NLSBoardLimitChecklistsReached', { limit: boardLimit })
    throw new Error(message)
  }

  // Check parent container limit
  let currentParentChecklists = 0
  if (parentCounterRef && parentCounterSnap) {
    const parentLimit = softCaps.maxChecklistsPerContainer
      ?? (fallbackLimits ? getWorkspaceLimitValue(fallbackLimits, 'maxChecklistsPerContainer', params.board as TimelineBoardKey) : WORKSPACE_COUNT_HARD_CAPS.maxChecklistsPerContainer)

    const pData = parentCounterSnap.exists() ? (parentCounterSnap.data() as Record<string, unknown>) : {}
    currentParentChecklists = typeof pData.checklists === 'number' ? pData.checklists : 0

    if (parentLimit !== -1 && currentParentChecklists + 1 > parentLimit) {
      if (typeof window !== 'undefined') {
        window.dispatchEvent(new CustomEvent('linkhub:paywall-required', {
          detail: {
            featureKey: CAP_KEY_TO_FEATURE_KEY.maxChecklistsPerContainer,
            limit: parentLimit,
            currentUsage: currentParentChecklists,
          },
        }))
      }
      const message = getNlsString('NLSParentLimitChecklistsReached', { limit: parentLimit })
      throw new Error(message)
    }
  }

  return {
    isCounted: true,
    applyWrites: () => {
      transaction.set(
        boardCounterRef,
        {
          checklists: currentBoardChecklists + 1,
          lastElementId: params.checklistId,
          updatedAt: serverTimestamp(),
        },
        { merge: true },
      )

      if (parentCounterRef) {
        transaction.set(
          parentCounterRef,
          {
            checklists: currentParentChecklists + 1,
            lastElementId: params.checklistId,
            updatedAt: serverTimestamp(),
          },
          { merge: true },
        )
      }
    },
  }
}

export async function checkChecklistsSubtreeRoom(
  db: Firestore,
  params: {
    workspaceId: string
    board: string
    parentContainerId: string
    subtreeSize: number
    fallbackLimits?: WorkspaceLimits | null
    store?: Map<string, unknown>
  },
): Promise<{
  ok: boolean
  message?: string
  featureKey?: string
  limit?: number
  currentUsage?: number
  roomLeft?: number
}> {
  const isCounted = isCountedChecklist({
    board: params.board,
    parentContainerId: params.parentContainerId,
  })

  if (!isCounted) {
    return { ok: true }
  }

  let limitsData: Record<string, unknown> | null
  let bData: Record<string, unknown>
  let pData: Record<string, unknown>

  if (params.store) {
    limitsData = (params.store.get(`workspaceEffectiveLimits/${params.workspaceId}`) as Record<string, unknown> | null) ?? null
    bData = (params.store.get(`workspaces/${params.workspaceId}/elementCounters/${params.board}`) as Record<string, unknown>) ?? {}
    pData = (params.store.get(`workspaces/${params.workspaceId}/elementParentCounters/${params.parentContainerId}`) as Record<string, unknown>) ?? {}
  } else {
    const effectiveLimitsSnap = await getDoc(doc(db, 'workspaceEffectiveLimits', params.workspaceId))
    limitsData = effectiveLimitsSnap.exists() ? (effectiveLimitsSnap.data() as Record<string, unknown>) : null
    const boardCounterSnap = await getDoc(doc(db, 'workspaces', params.workspaceId, 'elementCounters', params.board))
    bData = boardCounterSnap.exists() ? (boardCounterSnap.data() as Record<string, unknown>) : {}
    if (params.parentContainerId && !isSyntheticParentId(params.parentContainerId)) {
      const parentCounterSnap = await getDoc(doc(db, 'workspaces', params.workspaceId, 'elementParentCounters', params.parentContainerId))
      pData = parentCounterSnap.exists() ? (parentCounterSnap.data() as Record<string, unknown>) : {}
    } else {
      pData = {}
    }
  }

  const boardSoftCaps = (limitsData?.boardSoftCaps ?? {}) as Record<string, Record<string, number>>
  const softCaps = (limitsData?.softCaps ?? {}) as Record<string, number>
  const fallbackLimits = params.fallbackLimits

  // Board limit room
  const boardLimit = boardSoftCaps[params.board]?.maxChecklistsPerBoard
    ?? softCaps.maxChecklistsPerBoard
    ?? (fallbackLimits ? getWorkspaceLimitValue(fallbackLimits, 'maxChecklistsPerBoard', params.board as TimelineBoardKey) : WORKSPACE_COUNT_HARD_CAPS.maxChecklistsPerBoard)

  const currentBoardChecklists = typeof bData.checklists === 'number' ? bData.checklists : 0
  const roomLeftBoard = boardLimit === -1 ? Infinity : Math.max(0, boardLimit - currentBoardChecklists)

  // Parent limit room
  const hasRealParent = params.parentContainerId && !isSyntheticParentId(params.parentContainerId)
  const parentLimit = softCaps.maxChecklistsPerContainer
    ?? (fallbackLimits ? getWorkspaceLimitValue(fallbackLimits, 'maxChecklistsPerContainer', params.board as TimelineBoardKey) : WORKSPACE_COUNT_HARD_CAPS.maxChecklistsPerContainer)

  const currentParentChecklists = typeof pData.checklists === 'number' ? pData.checklists : 0
  const roomLeftParent = (!hasRealParent || parentLimit === -1) ? Infinity : Math.max(0, parentLimit - currentParentChecklists)

  const roomLeft = Math.min(roomLeftBoard, roomLeftParent)

  if (params.subtreeSize > roomLeft) {
    const isBoardBottleneck = roomLeftBoard <= roomLeftParent
    const featureKey = isBoardBottleneck ? CAP_KEY_TO_FEATURE_KEY.maxChecklistsPerBoard : CAP_KEY_TO_FEATURE_KEY.maxChecklistsPerContainer
    const limit = isBoardBottleneck ? boardLimit : parentLimit
    const currentUsage = isBoardBottleneck ? currentBoardChecklists : currentParentChecklists

    const message = getNlsString('NLSRestructureFailedSubtreeRoom', {
      subtreeSize: params.subtreeSize,
      roomLeft,
    })

    return {
      ok: false,
      message,
      featureKey,
      limit,
      currentUsage,
      roomLeft,
    }
  }

  return { ok: true }
}

export async function runChecklistCreateTransaction(
  db: Firestore,
  params: {
    workspaceId: string
    board: string
    parentContainerId: string
    checklistRef: DocumentReference
    checklistData: Record<string, unknown>
    fallbackLimits?: WorkspaceLimits | null
    extraWrites?: (transaction: Transaction) => void
  },
): Promise<void> {
  await runTransaction(db, async (transaction) => {
    const stepResult = await readChecklistCounterSteps(transaction, db, {
      workspaceId: params.workspaceId,
      board: params.board,
      parentContainerId: params.parentContainerId,
      checklistId: params.checklistRef.id,
      nodeKind: params.checklistData.nodeKind as string | undefined,
      isArchived: params.checklistData.isArchived as boolean | undefined,
      fallbackLimits: params.fallbackLimits,
    })

    transaction.set(params.checklistRef, params.checklistData)
    stepResult.applyWrites()
    if (params.extraWrites) {
      params.extraWrites(transaction)
    }
  })
}
